/**
 * Utility for determining colors based on call status and outcome
 */

/**
 * Gets the color for the Call Status display (typically for tooltips)
 * @param {string} displayStatus - The normalized display status (e.g., "Success", "Call")
 * @param {string} rawStatus - The raw status string from the API
 * @returns {string} Hex color code
 */
export const getCallStatusColor = (displayStatus, rawStatus) => {
    if (!displayStatus) return "#111827";
    if (displayStatus === "Success") return "#10b981";
    if (!rawStatus) return "#111827";
    const s = rawStatus.toLowerCase();
    if (s.includes("call not connect")) return "#ef4444";
    if (
        s.includes("not conclude") || s.includes("not interested") ||
        s.includes("no answer") || s.includes("not receive") ||
        s.includes("busy") || s.includes("cancel") || s.includes("fail")
    ) return "#ef4444";
    return "#10b981";
};

/**
 * Gets the color for the Call Outcome display
 * @param {string} outcome - The call outcome string
 * @returns {string} Hex color code
 */
// export const getCallOutcomeColor = (outcome) => {
//     if (!outcome) return "#111827";
//     const o = outcome.toLowerCase();
//     if (o.includes("no call")) return "#ef4444";
//     if (
//         (o.includes("concluded") && !o.includes("not")) ||
//         //(o.includes("interested") && !o.includes("not")) ||
//         o.includes("success")
//     ) return "#10b981";
//     if (
//         //o.includes("not concluded") || 
//         //o.includes("not conclude") ||
//         o.includes("not interested") || o.includes("no answer") ||
//         o.includes("not receive") || o.includes("busy") ||
//         o.includes("cancel") || o.includes("fail")
//     ) return "#ef4444";
//     if (o.includes('interested in future')) return "#7288c5ff";
//     if (o.includes('not concluded')) return "#C2410C";
//     return "#10b981";
// };

export const getCallOutcomeColor = (outcome) => {
    if (!outcome) return "#6B7280";

    const o = outcome.toLowerCase();

    // ❌ Negative
    if (
        o.includes("no call") ||
        o.includes("not interested") ||
        o.includes("no answer") ||
        o.includes("not receive") ||
        o.includes("busy") ||
        o.includes("cancel") ||
        o.includes("fail")
    ) return "#EF4444";

    // ⏳ Pending
    if (o.includes("not concluded") || o.includes("not conclude"))
        return "#0959cb";

    // 🔮 Future
    if (o.includes("interested in future"))
        return "#d8a465ff";

    // ✅ Positive
    if (
        (o.includes("concluded") && !o.includes("not")) ||
        o.includes("success")
    ) return "#10B981";

    // ⚪ Default
    return "#6B7280";
};

/**
 * Internal helper for default status background colors
 */
const getStatusBgColor = (status) => {
    if (!status) return "#6b7280";
    const s = status.toLowerCase();
    if (s.includes("success")) return "#10b981";
    if (s.includes("conclude") && !s.includes("not")) return "#10b981";
    if (s.includes("busy")) return "#fef3c7";
    if (s.includes("no answer") || s.includes("not receive")) return "rgb(255, 204, 0)";
    if (s.includes("cancel")) return "#FEE2E2";
    if (s.includes("fail") || s.includes("invalid number")) return "#FEE2E2";
    if (s.includes("not concluded")) return "#C026D3";
    if (s.includes("not interested")) return "#3b82f6";
    if (s.includes("call not connect")) return "#FEE2E2";
    return "#3b82f6";
};

/**
 * Internal helper for default status text colors
 */
const getStatusTextColor = (status) => {
    if (!status) return "#ffffff";
    const s = status.toLowerCase();
    if (s.includes("busy")) return "#92400e";
    if (s.includes("no answer") || s.includes("not receive")) return "#ffffff";
    if (s.includes("cancel") || s.includes("fail") || s.includes("call not connect") || s.includes("invalid number")) return "#B91C1C";
    return "#ffffff";
};

/**
 * Gets the consolidated styling for the status button
 * @param {string} displayStatus - The normalized display status
 * @param {string} rawStatus - The raw status string
 * @param {string} outcome - The call outcome string
 * @returns {object} Object containing backgroundColor, color, and iconColor
 */
export const getButtonColor = (displayStatus, rawStatus, outcome) => {
    const normalizedOutcome = String(outcome || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ");

    const isNotConcludedOutcome = normalizedOutcome.includes("not concluded");
    const shouldUsePurple = displayStatus === "Success" && isNotConcludedOutcome;

    const isNoCallOutcome = normalizedOutcome.includes("no call");
    const shouldUseGrey = displayStatus === "Call Not Connect" && isNoCallOutcome;

    const isInterestedInFutureOutcome = normalizedOutcome.includes("interested in future");
    const shouldUseFutureCyan = displayStatus === "Success" && isInterestedInFutureOutcome;

    const isNotInterestedOutcome = normalizedOutcome.includes("not interested");
    const shouldUseBlue = (displayStatus === "Success" || displayStatus === "Call Concluded") && isNotInterestedOutcome;

    const statusBgColor = getStatusBgColor(rawStatus || "");

    const backgroundColor = shouldUseGrey
        ? "#dc2626"
        : shouldUseFutureCyan
            ? "#ef921e"
            : shouldUsePurple
                ? "#C026D3"
                : shouldUseBlue
                    ? "#3b82f6"
                    : statusBgColor;

    const isRedTheme = backgroundColor === "#FEE2E2" || backgroundColor === "#dc2626";

    let color = "#ffffff";
    if (backgroundColor === "#FEE2E2") {
        color = "#B91C1C";
    } else if (backgroundColor === "#fef3c7" || backgroundColor === "rgb(255, 204, 0)" || backgroundColor === "#ef921e") {
        color = "#111827";
    }

    const isLightButton = backgroundColor === "#9CA3AF" || backgroundColor === "#FEE2E2";
    const isYellowBackground = backgroundColor === "#fef3c7";

    const iconColor = isYellowBackground
        ? color
        : isLightButton
            ? color
            : "#ffffff";

    return { backgroundColor, color, iconColor };
};
